import { T } from "@/lib/i18n";
import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Check, CheckCheck } from "lucide-react";

const time = (iso) => new Date(iso).toLocaleString("it-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const Ticks = ({ m }) => {
  if (m.read_at) return <span data-testid={`tick-read-${m.id}`} title={`Letto ${time(m.read_at)}`} className="ml-1 inline-flex items-center gap-1 text-[#ff5a5f] font-semibold">
    <CheckCheck className="w-3.5 h-3.5 text-[#ff5a5f]" /> {T("Letto")} {time(m.read_at).split(", ").pop()}</span>;
  if (m.delivered_at) return <span data-testid={`tick-delivered-${m.id}`} title={`Consegnato ${time(m.delivered_at)}`} className="ml-1"><CheckCheck className="w-3.5 h-3.5 text-white/50" /></span>;
  return <span data-testid={`tick-sent-${m.id}`} title={T("Inviato")} className="ml-1"><Check className="w-3.5 h-3.5 text-white/50" /></span>;
};

// Shared chat thread UI. `me` = "client" | "admin"; dark for SU panel.
export const ChatThread = ({ messages, me, onSend, dark, placeholder, testid = "chat", renderTools, typing, typingLabel, onTyping }) => {
  const [text, setText] = useState("");
  const lastPing = useRef(0);
  const change = (v) => {
    setText(v);
    if (onTyping && v.trim() && Date.now() - lastPing.current > 2500) { lastPing.current = Date.now(); onTyping(); }
  };
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [messages.length, typing]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await onSend(text.trim()); setText(""); } finally { setBusy(false); }
  };
  const bubble = (mine) => mine
    ? `${dark ? "bg-[#3a4047]" : "bg-[#2B3036]"} text-white ml-auto`
    : dark ? "bg-[#27272a] text-white" : "bg-white border border-[#e4e4e7] text-[#2B3036]";
  return (
    <div data-testid={`${testid}-thread`} className="flex flex-col h-[420px]">
      <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${dark ? "bg-[#09090b]" : "bg-[#f4f6f7]"}`}>
        {messages.map((m) => (
          <div key={m.id} data-testid={`${testid}-msg-${m.id}`} className={`max-w-[80%] w-fit px-3.5 py-2.5 text-sm ${bubble(m.sender === me)}`}>
            <div className="whitespace-pre-wrap break-words">{m.text}</div>
            <div className={`text-[10px] font-mono mt-1 flex items-center gap-1 ${m.sender === me ? "text-white/70" : "text-[#71717a]"}`}>
              {m.sender === "admin" ? "Aurora-ADV" : "Cliente"} · {time(m.created_at)}
              {m.broadcast_id && <span className="uppercase">{T("· a tutti")}</span>}
              {m.sender === me && <Ticks m={m} />}
            </div>
          </div>
        ))}
        {typing && (
          <div data-testid={`${testid}-typing`} className={`w-fit px-3.5 py-2 text-xs italic flex items-center gap-2 ${dark ? "bg-[#27272a] text-[#a1a1aa]" : "bg-white border border-[#e4e4e7] text-[#5b6168]"}`}>
            <span className="flex gap-0.5">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</span>
            {typingLabel} sta scrivendo…
          </div>
        )}
        {!messages.length && <div className="text-center text-xs font-mono text-[#71717a] py-10">{T("Nessun messaggio")}</div>}
        <div ref={end} />
      </div>
      {renderTools && renderTools((t) => change(t))}
      <form onSubmit={send} className={`flex border-t ${dark ? "border-[#27272a]" : "border-[#e4e4e7]"}`}>
        <textarea data-testid={`${testid}-input`} value={text} maxLength={2000} rows={2} placeholder={placeholder}
          onChange={(e) => change(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
          className={`flex-1 resize-none p-3 text-sm outline-none ${dark ? "bg-[#18181b] text-white placeholder:text-[#52525b]" : "bg-white"}`} />
        <button data-testid={`${testid}-send`} disabled={busy || !text.trim()}
          className="w-14 aurora-gradient text-white flex items-center justify-center disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
};
